/**
 * Board Locks Hook (PostgreSQL)
 *
 * Replaces Convex boardLocks queries/mutations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_BASE = '/api/board-locks';

interface LockResponse {
  boardId: string;
  userId: string;
  userName: string;
  lockedAt: number;
}

interface GetLockResponse {
  lock: LockResponse | null;
}

interface AcquireLockResponse {
  success: boolean;
  lock?: LockResponse;
  message?: string;
}

interface RefreshLockResponse {
  success: boolean;
  lock?: LockResponse;
  message?: string;
}

interface ReleaseLockResponse {
  success: boolean;
  released: boolean;
}

/**
 * Get current lock status for a board
 * Fetches on mount and when invalidated (no polling)
 */
export function useBoardLock(boardId: string | undefined) {
  return useQuery({
    queryKey: ['boardLock', boardId],
    queryFn: async (): Promise<LockResponse | null> => {
      if (!boardId) return null;
      const res = await fetch(`${API_BASE}?boardId=${encodeURIComponent(boardId)}`);
      if (!res.ok) {
        throw new Error(`Failed to fetch lock: ${res.status}`);
      }
      const data: GetLockResponse = await res.json();
      return data.lock;
    },
    enabled: !!boardId,
    staleTime: 30 * 1000, // 30 seconds - locks need fresher data
    refetchOnMount: true, // Always check lock status on mount
  });
}

/**
 * Acquire a lock on a board
 */
export function useAcquireLock() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      boardId,
      userId,
      userName,
    }: {
      boardId: string;
      userId: string;
      userName: string;
    }): Promise<AcquireLockResponse> => {
      const res = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'acquire', boardId, userId, userName }),
      });
      if (!res.ok) {
        throw new Error(`Failed to acquire lock: ${res.status}`);
      }
      return res.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['boardLock', variables.boardId] });
    },
  });
}

/**
 * Release a lock on a board
 */
export function useReleaseLock() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      boardId,
      userId,
    }: {
      boardId: string;
      userId: string;
    }): Promise<ReleaseLockResponse> => {
      const res = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'release', boardId, userId }),
      });
      if (!res.ok) {
        throw new Error(`Failed to release lock: ${res.status}`);
      }
      return res.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['boardLock', variables.boardId] });
    },
  });
}

/**
 * Refresh a lock to keep it alive
 */
export function useRefreshLock() {
  return useMutation({
    mutationFn: async ({
      boardId,
      userId,
    }: {
      boardId: string;
      userId: string;
    }): Promise<RefreshLockResponse> => {
      const res = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'refresh', boardId, userId }),
      });
      if (!res.ok) {
        throw new Error(`Failed to refresh lock: ${res.status}`);
      }
      return res.json();
    },
  });
}
